import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, ListMusic, Play } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export default function MusicPlayer() {
  const { data: tracks = [] } = useQuery({
    queryKey: ["tracks"],
    queryFn: async () => (await api.get("/tracks")).data,
    staleTime: 60_000,
  });

  const [current, setCurrent] = useState(() => localStorage.getItem("nfc_track") || null);
  const [enabled, setEnabled] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [embedStarted, setEmbedStarted] = useState(false);
  const audioRef = useRef(null);
  const embedRef = useRef(null);

  const track =
    tracks.find((t) => (t.audio_url || t.embed_url) === current) || tracks[0] || null;
  const isEmbed = track?.kind === "embed";
  const src = track && !isEmbed ? track.audio_url : null;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    if (enabled) {
      audio.volume = 0.45;
      audio.loop = true;
      audio.play().catch(() => setBlocked(true));
    } else {
      audio.pause();
    }
  }, [enabled, src]);

  useEffect(() => {
    if (!blocked) return;
    const unlock = () => {
      audioRef.current
        ?.play()
        .then(() => setBlocked(false))
        .catch(() => {});
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [blocked]);

  if (!track) return null;

  const choose = (t) => {
    setCurrent(t.audio_url || t.embed_url);
    localStorage.setItem("nfc_track", t.audio_url || t.embed_url);
    setPickerOpen(false);
    setEnabled(true);
    setEmbedStarted(false);
    audioRef.current?.pause();
  };

  return (
    <>
      {src && <audio ref={audioRef} src={src} preload="auto" />}
      <div className="fixed bottom-6 left-6 z-[65] flex items-center gap-2">
        {!isEmbed && (
          <motion.button
            data-testid="music-toggle-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.6, duration: 0.5 }}
            onClick={() => setEnabled((v) => !v)}
            aria-label={enabled ? "Disattiva musica" : "Attiva musica"}
            className="relative w-12 h-12 rounded-full border border-[#C5A059]/50 bg-[#0A0A0B]/85 backdrop-blur flex items-center justify-center text-[#C5A059] hover:border-[#C5A059] hover:shadow-[0_0_20px_rgba(197,160,89,0.35)] transition-all"
          >
            {enabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            {enabled && !blocked && (
              <span className="absolute inset-0 rounded-full border border-[#C5A059]/40 animate-ping" />
            )}
          </motion.button>
        )}
        {tracks.length > 0 && (
          <motion.button
            data-testid="music-picker-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.7, duration: 0.5 }}
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="Scegli il brano"
            className="w-12 h-12 rounded-full border border-[#C5A059]/50 bg-[#0A0A0B]/85 backdrop-blur flex items-center justify-center text-[#C5A059] hover:border-[#C5A059] transition-all"
          >
            <ListMusic className="w-5 h-5" />
          </motion.button>
        )}
      </div>

      {isEmbed && (
        <motion.div
          data-testid="apple-music-player"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 left-6 z-[65] w-[300px] max-w-[calc(100vw-3rem)]"
        >
          <p className="mb-1.5 px-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
            {track.title} — {track.artist}
          </p>
          <div className="relative">
            <iframe
              ref={embedRef}
              title={`${track.title} — ${track.artist}`}
              allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
              height="150"
              style={{ width: "100%", overflow: "hidden", borderRadius: "10px", background: "transparent" }}
              sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
              src={track.embed_url}
            />
            {!embedStarted && (
              <motion.button
                data-testid="apple-music-play-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => {
                  setEmbedStarted(true);
                  // Ricarica il player dentro il gesto dell'utente: i browser
                  // consentono l'autoplay solo se il frame viene caricato dopo un tocco
                  if (embedRef.current) {
                    embedRef.current.src = track.embed_url;
                  }
                }}
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/60 backdrop-blur-sm rounded-[10px] border border-[#C5A059]/40 hover:bg-black/50 transition-colors cursor-pointer"
              >
                <Play className="w-8 h-8 text-[#C5A059]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">Tocca per avviare</span>
              </motion.button>
            )}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            data-testid="music-track-picker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="fixed bottom-24 left-6 z-[65] w-72 bg-[#121215] border border-[#C5A059]/30 p-2"
          >
            <p className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D]">
              Scegli il brano
            </p>
            {tracks.map((t) => (
              <button
                key={t.id}
                data-testid={`track-option-${t.id}`}
                onClick={() => choose(t)}
                className={`w-full text-left px-3 py-2.5 transition-colors ${
                  (t.audio_url || t.embed_url) === (track.audio_url || track.embed_url)
                    ? "bg-[#C5A059]/15 border-l-2 border-[#C5A059]"
                    : "hover:bg-[#1C1C21] border-l-2 border-transparent"
                }`}
              >
                <p className="text-sm text-[#F4EBE1] font-semibold">
                  {t.title}
                  {t.kind === "embed" && (
                    <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] text-[#FA57C1]">Apple Music</span>
                  )}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">{t.artist}</p>
              </button>
            ))}
          </motion.div>
        )}
        {blocked && (
          <motion.p
            data-testid="music-unlock-hint"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-24 left-6 z-[65] font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D] bg-[#121215] border border-[#C5A059]/25 px-3 py-2"
          >
            Tocca per attivare la musica
          </motion.p>
        )}
      </AnimatePresence>
    </>
  );
}
